import React from 'react';
import { CheckCircle, XCircle, Loader2, UserPlus, Users, UserX } from 'lucide-react';
import { pickLocalizedText } from '../../utils/localizedContent';

export default function ParticipantsList({ 
  event,
  participants, 
  hasEventsPermission, 
  isAdmin,
  onAdminCheckIn, 
  onRemoveParticipant,
  onAddParticipant,
  actionLoading,
  lang 
}) {
  const isTournamentDeployed = event?.event_type === 'tournament' && !!event?.participant_organization;
  const isTournamentStarted = ['in_progress', 'completed'].includes(event?.status);
  const isTournamentFull = (event?.max_participants || 0) > 0 && participants.total_registered >= event.max_participants;
  const canAddParticipant = !isTournamentStarted && !isTournamentFull;

  const addParticipantTooltip = isTournamentStarted
    ? pickLocalizedText(lang, 'No se pueden anadir participantes con el torneo iniciado.', 'Cannot add participants after tournament has started.')
    : isTournamentFull
    ? pickLocalizedText(lang, 'El torneo esta completo.', 'Tournament is full.')
    : pickLocalizedText(lang, 'Crea un participante temporal para este torneo.', 'Create a temporary participant for this tournament.');

  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-foreground font-bold inline-flex items-center gap-2">
          <Users size={16} className="text-primary" />
          {pickLocalizedText(lang, 'Participantes', 'Participants')}
        </h3>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            {participants.total_checked_in} {pickLocalizedText(lang, 'check-ins', 'checked-in')}
          </span>
          <span className="px-2 py-1 rounded bg-muted/50 text-muted-foreground border border-border">
            {participants.total_registered} {pickLocalizedText(lang, 'inscritos', 'registered')}
          </span>
        </div>
      </div>
      
      {participants.participants.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">
          {isTournamentDeployed
            ? pickLocalizedText(lang, 'Participantes desplegados en competicion. Cancela el torneo para volver a mostrarlos aqui.', 'Participants are deployed in competition. Cancel tournament to show them here again.')
            : pickLocalizedText(lang, 'Aun no hay participantes', 'No participants yet')}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {participants.participants.map(p => (
            <ParticipantCard 
              key={p.user_id}
              participant={p}
              event={event}
              hasEventsPermission={hasEventsPermission}
              onAdminCheckIn={onAdminCheckIn}
              onRemoveParticipant={onRemoveParticipant}
              actionLoading={actionLoading}
              lang={lang}
            />
          ))}
        </div>
      )}

      {/* Admin actions */}
      {event.event_type === 'tournament' && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex flex-wrap justify-center gap-3">
            {isAdmin && !isTournamentStarted && (
              <button
                onClick={onAddParticipant}
                disabled={actionLoading === 'add-participant' || !canAddParticipant}
                className="px-6 py-3 text-sm rounded-xl border border-[#10B981]/30 bg-[#10B981]/10 text-[#86EFAC] font-semibold flex items-center gap-2 disabled:opacity-60"
                data-testid="add-participant-btn"
                title={addParticipantTooltip}
              >
                {actionLoading === 'add-participant' ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
                {pickLocalizedText(lang, 'Anadir Participante', 'Add Participant')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ParticipantCard({ participant: p, event, hasEventsPermission, onAdminCheckIn, onRemoveParticipant, actionLoading, lang }) {
  const canRemove = event?.event_type === 'tournament' && hasEventsPermission && !p.is_checked_in;
  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-xl border ${
        p.is_checked_in ? 'bg-gradient-to-r from-emerald-500/10 to-card border-emerald-500/30' : 'bg-card border-border'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
          {p.name?.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="text-foreground text-sm font-semibold">{p.name}</p>
        </div>
      </div>
      
      {p.is_checked_in ? (
        <CheckCircle size={18} className="text-[#10B981]" />
      ) : hasEventsPermission ? (
        <div className="flex items-center gap-1.5">
          <button 
            onClick={() => onAdminCheckIn(p.user_id)} 
            disabled={actionLoading === `checkin-${p.user_id}`} 
            className="px-2 py-1 text-xs bg-primary/20 text-primary rounded hover:bg-primary/30"
          >
            {actionLoading === `checkin-${p.user_id}` ? <Loader2 size={12} className="animate-spin" /> : 'Check-in'}
          </button>
          {canRemove && (
            <button
              onClick={() => onRemoveParticipant?.(p.user_id)}
              disabled={actionLoading === `remove-${p.user_id}`}
              className="px-2 py-1 text-xs bg-[#EF4444]/15 text-[#F87171] rounded hover:bg-[#EF4444]/25 inline-flex items-center gap-1"
              title={pickLocalizedText(lang, 'Eliminar participante', 'Remove participant')}
            >
              {actionLoading === `remove-${p.user_id}` ? <Loader2 size={12} className="animate-spin" /> : <UserX size={12} />}
            </button>
          )}
        </div>
      ) : (
        <XCircle size={18} className="text-muted-foreground/50" />
      )}
    </div>
  );
}
